/**
 * Admin Service - API calls for admin dashboard and management
 * Connects to backend AdminController endpoints
 */

import axios from 'axios';
import { API_BASE_URL } from '../config/api.config';

// Configure axios defaults with central API_BASE_URL

const adminApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Redirect to login if unauthorized
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Dashboard Types
export interface DashboardStats {
  today: {
    newBookings: number;
    revenue: number;
    newCustomers: number;
  };
  thisWeek: {
    newBookings: number;
    revenue: number;
    scheduledTrips: number;
    totalParticipants: number;
  };
  thisMonth: {
    newBookings: number;
    revenue: number;
    scheduledTrips: number;
    averageGroupSize: number;
  };
  upcomingTrips: TripAlert[];
  recentBookings: RecentBooking[];
  systemAlerts: SystemAlert[];
}

export interface TripAlert {
  floatTripId: number;
  tripDate: string;
  alertType: string;
  message: string;
  currentParticipants: number;
  minimumRequired: number;
  priority: string;
}

export interface RecentBooking {
  id: number;
  bookingNumber: string;
  customerName: string;
  participantCount: number;
  tripDate: string;
  totalAmount: number;
  createdAt: string;
}

export interface SystemAlert {
  id: number;
  alertType: string;
  title: string;
  message: string;
  severity: string;
  isRead: boolean;
  createdAt: string;
  relatedEntityType?: string;
}

// Customer Types
export interface Customer {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  companyName?: string;
  customerType: 'Private' | 'Business';
  totalBookings: number;
  totalRevenue: number;
  isVip: boolean;
  createdAt: string;
  lastBookingDate?: string;
  addresses?: CustomerAddress[];
  notes?: string;
}

export interface CustomerAddress {
  id: number;
  addressType: string;
  street: string;
  city: string;
  postalCode: string;
  country: string;
  isDefault: boolean;
}

// Booking Types
export interface Booking {
  id: number;
  bookingNumber: string;
  quoteNumber?: string; // For Quote-type bookings: ANG-2026-001
  customerId: number;
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  scheduleId: number;
  tripDate: string;
  tripTime?: string;
  tripType?: string;
  tourTypeName?: string;
  participantCount: number;
  variant: string;
  bookingType?: 'Quote' | 'Regular' | 'Option' | 'Manual';
  status: string; // Extended with OfferSent, OfferAccepted, OfferDeclined, OfferExpired
  totalPrice: number;
  totalAmount?: number;
  depositPaid: boolean;
  fullyPaid: boolean;
  paymentStatus: string;
  createdAt: string;
  updatedAt?: string;
  quoteValidUntil?: string; // Gültig bis für Angebote
  notes?: string;
  meals?: BookingMeal[];
  extras?: BookingExtra[];
}

export interface BookingMeal {
  mealType: string;
  quantity: number;
  pricePerUnit: number;
}

export interface BookingExtra {
  extraType: string;
  quantity: number;
  pricePerUnit: number;
}

// Payment Types
export interface Payment {
  id: number;
  bookingId: number;
  bookingNumber: string;
  customerName: string;
  amount: number;
  paymentType: 'Deposit' | 'FinalPayment' | 'Refund';
  paymentMethod: 'BankTransfer' | 'PayPal' | 'Stripe' | 'Cash';
  status: 'Pending' | 'Completed' | 'Failed' | 'Refunded';
  transactionId?: string;
  paidAt?: string;
  createdAt: string;
}

// Schedule Types
export interface Schedule {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
  maxParticipants: number;
  bookedParticipants: number;
  availableSpots: number;
  isBookable: boolean;
  hasFlexibleBookings: boolean;
  status: string;
  capacityPercentage: number;
  alerts: string[];
  variants: string[];
}

// Report Types
export interface ReportData {
  reportType: string;
  dateRange: {
    from: string;
    to: string;
  };
  data: any;
  summary: any;
}

// Invoice Type Keys for text templates
export type InvoiceTypeKey = 'Deposit' | 'Final' | 'ShortTerm' | 'Cancellation' | 'Credit' | 'Correction';

// Invoice text templates - keyed by invoice type
export interface InvoiceTextTemplates {
  Deposit?: string;
  Final?: string;
  ShortTerm?: string;
  Cancellation?: string;
  Credit?: string;
  Correction?: string;
}

// Settings Types - Company Settings from Backend
export interface CompanySettings {
  id: number;
  // Company Info
  companyName: string;
  companyStreet: string;
  companyPostalCode: string;
  companyCity: string;
  companyPhone: string;
  companyEmail: string;
  companyWebsite: string;
  // Legal
  ceoName: string;
  tradeRegisterNumber: string;
  tradeRegisterCourt: string;
  vatNumber: string;
  // Complementary (GmbH & Co. KG)
  complementaryCompany: string;
  complementaryRegisterNumber: string;
  // Bank
  bankName: string;
  bankIban: string;
  bankBic: string;
  // System
  defaultCurrency: string;
  timezone: string;
  logoPath: string;
  // Booking
  minParticipants: number;
  maxParticipants: number;
  depositPercentage: number;
  // MwSt-Sätze (Anlage v1.3 Abschnitt 2.1 Punkt 8), Dezimalwerte (0.19 = 19%)
  vatRateStandard?: number;
  vatRateReduced?: number;
  // Payment Methods (enabled/disabled toggles)
  stripeEnabled: boolean;
  payPalEnabled: boolean;
  klarnaEnabled: boolean;
  bankTransferEnabled: boolean;
  // Invoice Text Templates (per invoice type)
  invoiceIntroTexts: InvoiceTextTemplates;
  invoiceOutroTexts: InvoiceTextTemplates;
  // Audit
  createdAt: string;
  updatedAt: string;
}

// Legacy Settings Types (for backwards compatibility)
export interface SystemSettings {
  bookingSettings: {
    minimumParticipants: number;
    maximumParticipants: number;
    depositPercentage: number;
    finalPaymentDaysBefore: number;
    cancellationDaysBefore: number;
  };
  emailSettings: {
    smtpHost: string;
    smtpPort: number;
    senderEmail: string;
    senderName: string;
  };
  paymentSettings: {
    stripeEnabled: boolean;
    paypalEnabled: boolean;
    bankTransferEnabled: boolean;
  };
  notificationSettings: {
    emailEnabled: boolean;
    whatsappEnabled: boolean;
    smsEnabled: boolean;
  };
}

// Blankoposition (Anlage v1.3 Abschnitt 2.5 Punkt 8): freie Rechnungsposition je Buchung
export interface BookingCustomItem {
  id: number;
  bookingId: number;
  description: string;
  quantity: number;
  grossPrice: number;
  taxRate: number; // Dezimalwert: 0.19, 0.07 oder 0
  total?: number;
  newBookingTotal?: number;
  createdBy?: string;
  createdAt?: string;
}

// AddOn Types (Zusatzoptionen)
export interface AddOnCategory {
  id: number;
  name: string;
  description: string;
  iconClass?: string;
  sortOrder: number;
  isActive: boolean;
  isVisibleToCustomers: boolean;
  requiresManualApproval: boolean;
  services?: AddOnService[];
  createdAt?: string;
  updatedAt?: string;
}

export interface AddOnService {
  id: number;
  categoryId: number;
  categoryName?: string;
  name: string;
  description: string;
  price: number; // NET price (informational)
  grossPrice: number; // BRUTTO-FIRST: Authoritative gross price shown to customers
  pricingType: 'PerPerson' | 'PerGroup' | 'PerDay' | 'PerNight' | 'Fixed';
  taxRate: number;
  isActive: boolean;
  sortOrder: number;
  minQuantityPerBooking?: number;
  maxQuantityPerBooking?: number;
  availableCapacity?: number;
  serviceDuration?: string; // TimeSpan as string
  setupTime?: string;
  cleanupTime?: string;
  deliveryInstructions?: string; // Delivery instructions
  cancellationPolicy?: string; // Cancellation policy
  requiresManualConfirmation: boolean;
  requiredLeadTimeHours?: number;
  providerName?: string;
  providerContact?: string;
  providerCost?: number;
  isComposite?: boolean; // Flag for composite add-ons
  isOptionalChoice?: boolean; // Flag for optional choice add-ons (e.g., meal selection where total must equal participant count)
  compositions?: CompositeAddOnComponent[]; // Components for composite add-ons
  bedsPerUnit?: number; // Number of beds per unit (for accommodation add-ons)
  isAccommodation?: boolean; // Flag for accommodation add-ons (requires bed validation)
  isKurtaxe?: boolean; // Flag for Kurtaxe (tourist tax - staatliche Abgabe, 0% MwSt)
  backofficeOnly?: boolean; // Nur Backoffice (Anlage v1.3 Abschnitt 2.1 Punkt 10): online unsichtbar
  createdAt?: string;
  updatedAt?: string;
}

// Composite Add-on Component Types
export interface CompositeAddOnComponent {
  id?: number;
  parentAddOnId: number;
  childAddOnId: number;
  childName: string;
  childPrice: number; // NET price
  childGrossPrice: number; // BRUTTO-FIRST: Gross price
  childTaxRate: number; // Tax rate (e.g., 0.07 for 7%, 0.19 for 19%)
  quantity: number;
  sortOrder: number;
  totalPrice: number; // NET total
  totalGrossPrice: number; // BRUTTO-FIRST: Gross total
}

export interface CreateCompositeComponentDto {
  childAddOnId: number;
  quantity: number;
  sortOrder?: number;
}

export interface UpdateCompositeComponentDto {
  quantity?: number;
  sortOrder?: number;
}

export interface CalculatedPriceResponse {
  calculatedPrice: number;
  components: {
    childAddOnId: number;
    childName: string;
    quantity: number;
    childPrice: number;
    totalPrice: number;
  }[];
}

// NEW: Tax breakdown interfaces
export interface TaxBreakdownDto {
  taxRate: number;
  netAmount: number;
  taxAmount: number;
  grossAmount: number;
  components: CompositeAddOnComponent[];
}

export interface CalculatedPriceWithTaxDto {
  totalNet: number;
  totalTax: number;
  totalGross: number;
  calculatedPrice: number; // Same as totalNet for backward compatibility
  components: CompositeAddOnComponent[];
  taxBreakdown: TaxBreakdownDto[];
}

export interface CreateAddOnCategoryDto {
  name: string;
  description: string;
  iconClass?: string;
  sortOrder?: number;
  isActive?: boolean;
  isVisibleToCustomers?: boolean;
  requiresManualApproval?: boolean;
}

export interface UpdateAddOnCategoryDto {
  name?: string;
  description?: string;
  iconClass?: string;
  sortOrder?: number;
  isActive?: boolean;
  isVisibleToCustomers?: boolean;
  requiresManualApproval?: boolean;
}

export interface CreateAddOnServiceDto {
  categoryId: number;
  name: string;
  description: string;
  price?: number; // NET price (optional, calculated from grossPrice)
  grossPrice: number; // BRUTTO-FIRST: Required gross price
  pricingType: 'PerPerson' | 'PerGroup' | 'PerDay' | 'PerNight' | 'Fixed';
  taxRate?: number;
  isActive?: boolean;
  sortOrder?: number;
  minQuantityPerBooking?: number;
  maxQuantityPerBooking?: number;
  availableCapacity?: number;
  serviceDuration?: string;
  setupTime?: string;
  cleanupTime?: string;
  requiresManualConfirmation?: boolean;
  requiredLeadTimeHours?: number;
  providerName?: string;
  providerContact?: string;
  providerCost?: number;
  isComposite?: boolean; // Flag for composite add-ons
  isOptionalChoice?: boolean; // Flag for optional choice add-ons (e.g., meal selection)
  bedsPerUnit?: number; // Number of beds per unit (for accommodation add-ons)
  isAccommodation?: boolean; // Flag for accommodation add-ons (requires bed validation)
  isKurtaxe?: boolean; // Flag for Kurtaxe (tourist tax - staatliche Abgabe, 0% MwSt)
  backofficeOnly?: boolean; // Nur Backoffice (Anlage v1.3 Abschnitt 2.1 Punkt 10): online unsichtbar
}

export interface UpdateAddOnServiceDto {
  name?: string;
  description?: string;
  price?: number; // NET price (optional, calculated from grossPrice)
  grossPrice: number; // BRUTTO-FIRST: Required gross price
  pricingType?: 'PerPerson' | 'PerGroup' | 'PerDay' | 'PerNight' | 'Fixed';
  taxRate?: number;
  isActive?: boolean;
  sortOrder?: number;
  minQuantityPerBooking?: number;
  maxQuantityPerBooking?: number;
  availableCapacity?: number;
  serviceDuration?: string;
  setupTime?: string;
  cleanupTime?: string;
  requiresManualConfirmation?: boolean;
  requiredLeadTimeHours?: number;
  providerName?: string;
  providerContact?: string;
  providerCost?: number;
  isComposite?: boolean; // Flag for composite add-ons
  isOptionalChoice?: boolean; // Flag for optional choice add-ons (e.g., meal selection)
  bedsPerUnit?: number; // Number of beds per unit (for accommodation add-ons)
  isAccommodation?: boolean; // Flag for accommodation add-ons (requires bed validation)
  isKurtaxe?: boolean; // Flag for Kurtaxe (tourist tax - staatliche Abgabe, 0% MwSt)
  backofficeOnly?: boolean; // Nur Backoffice (Anlage v1.3 Abschnitt 2.1 Punkt 10): online unsichtbar
}

// Booking AddOn DTO - für AddOns einer bestehenden Buchung
export interface BookingAddOnDto {
  id: number;
  bookingId: number;
  addOnServiceId: number;
  serviceName: string;
  serviceDescription?: string;
  categoryName?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  taxRate: number;
  taxAmount?: number;
  specialRequests?: string;
  status?: string;
  isLateAddition?: boolean;
  isCancelled?: boolean;
}

// CalculatedAddonDto - für berechnete AddOns mit Kategorisierungs-Info
export interface CalculatedAddonDto {
  addOnServiceId: number;
  addOnServiceName: string;
  pricingType: string;
  quantityTotal: number;
  quantityFree: number;
  quantityPaid: number;
  pricePerUnit: number;
  grossPricePerUnit: number;
  taxRate: number;
  subtotal: number;
  isOptionalChoice: boolean;
  isAccommodation: boolean;
  bedsPerUnit?: number;
  isIncludedInTourPrice: boolean;
  categoryId?: number;
  quantityMultiplier?: number;
}

// ========================================
// TOUR TYPE INTERFACE
// ========================================

export interface TourType {
  id: number;
  code: string;
  name: string;
  description?: string;
  basePricePerPerson: number;
  minParticipants: number;
  maxParticipants: number;
  sortOrder: number;
  isActive: boolean;
  colorCode?: string;
  iconName?: string;
  pricingMode?: string;
  fixedPrice?: number;
  createdAt: string;
  updatedAt: string;
}

// ========================================
// QUOTE TYPES (#35 - Angebote erstellen)
// ========================================

export type QuoteStatus = 'Draft' | 'Sent' | 'Accepted' | 'Declined' | 'Expired';

export interface Quote {
  id: number;
  quoteNumber: string;
  customerId?: number;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  customerCompany?: string;
  tourTypeId: number;
  tourTypeName: string;
  tourDate: string;
  tourTime?: string;
  participantCount: number;
  basePrice: number;
  addonsPrice: number;
  totalGross: number;
  totalNet: number;
  totalVat: number;
  status: QuoteStatus;
  statusDisplayName: string;
  validUntil: string;
  isExpired: boolean;
  daysUntilExpiration: number;
  canEdit: boolean;
  canSend: boolean;
  canConvert: boolean;
  customerNotes?: string;
  internalNotes?: string;
  sentAt?: string;
  acceptedAt?: string;
  convertedBookingId?: number;
  convertedBookingNumber?: string;
  createdAt: string;
  createdById: number;
  createdByName: string;
  addons: QuoteAddonDetail[];
}

export interface QuoteAddonDetail {
  id: number;
  addonServiceId: number;
  addonServiceName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  notes?: string;
}

export interface QuoteListItem {
  id: number;
  quoteNumber: string;
  customerName: string;
  customerEmail: string;
  tourTypeName: string;
  tourDate: string;
  participantCount: number;
  totalGross: number;
  status: QuoteStatus;
  statusDisplayName: string;
  validUntil: string;
  isExpired: boolean;
  createdAt: string;
  convertedBookingId?: number;
}

export interface QuoteFilter {
  status?: QuoteStatus;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  tourTypeId?: number;
  includeExpired?: boolean;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: string;
}

export interface CreateQuoteDto {
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  customerCompany?: string;
  customerId?: number;
  tourTypeId: number;
  tourDate: string;
  tourTime?: string;
  participantCount: number;
  addons: QuoteAddonInput[];
  customerNotes?: string;
  internalNotes?: string;
  validDays?: number;
}

export interface UpdateQuoteDto {
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  customerCompany?: string;
  customerId?: number;
  tourTypeId: number;
  tourDate: string;
  tourTime?: string;
  participantCount: number;
  addons: QuoteAddonInput[];
  customerNotes?: string;
  internalNotes?: string;
  validUntil?: string;
}

export interface QuoteAddonInput {
  addonServiceId: number;
  quantity: number;
  notes?: string;
}

export interface QuoteStatusUpdate {
  status: QuoteStatus;
  reason?: string;
}

export interface SendQuoteEmail {
  recipientEmail?: string;
  customMessage?: string;
  sendCopyToAdmin?: boolean;
}

export interface QuotePricePreview {
  tourTypeId: number;
  tourDate: string;
  participantCount: number;
  addons: QuoteAddonInput[];
}

export interface QuotePricePreviewResponse {
  basePrice: number;
  basePricePerPerson: number;
  addonsPrice: number;
  totalGross: number;
  totalNet: number;
  totalVat: number;
  vatRate: number;
  addonBreakdown: {
    addonServiceId: number;
    name: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
  }[];
}

export interface QuoteListResponse {
  items: QuoteListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Admin Service API Methods
class AdminService {
  // Dashboard - Backend gibt camelCase zurück (System.Text.Json.JsonNamingPolicy.CamelCase)
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await adminApi.get('/admin/dashboard');
    const data = response.data;

    // Backend gibt camelCase zurück (ASP.NET Core Standard-Konfiguration)
    return {
      today: {
        newBookings: data.today?.newBookings || 0,
        revenue: data.today?.revenue || 0,
        newCustomers: data.today?.newCustomers || 0
      },
      thisWeek: {
        newBookings: data.thisWeek?.newBookings || 0,
        revenue: data.thisWeek?.revenue || 0,
        scheduledTrips: data.thisWeek?.scheduledTrips || 0,
        totalParticipants: data.thisWeek?.totalParticipants || 0
      },
      thisMonth: {
        newBookings: data.thisMonth?.newBookings || 0,
        revenue: data.thisMonth?.revenue || 0,
        scheduledTrips: data.thisMonth?.scheduledTrips || 0,
        averageGroupSize: data.thisMonth?.averageGroupSize || 0
      },
      upcomingTrips: (data.upcomingTrips || []).map((trip: any) => ({
        floatTripId: trip.floatTripId || trip.id,
        tripDate: trip.tripDate,
        alertType: trip.alertType || '',
        message: trip.message || '',
        currentParticipants: trip.currentParticipants || 0,
        minimumRequired: trip.minimumRequired || 30,
        priority: trip.priority || 'normal'
      })),
      recentBookings: (data.recentBookings || []).map((b: any) => ({
        id: b.id,
        bookingNumber: b.bookingNumber,
        customerName: b.customerName,
        participantCount: b.participantCount,
        tripDate: b.tripDate,
        totalAmount: b.totalPrice || b.totalAmount,
        createdAt: b.createdAt
      })),
      systemAlerts: (data.systemAlerts || []).map((alert: any) => ({
        id: alert.id,
        alertType: alert.alertType,
        title: alert.title,
        message: alert.message,
        severity: alert.severity,
        isRead: alert.isRead,
        createdAt: alert.createdAt,
        relatedEntityType: alert.relatedEntityType
      }))
    };
  }

  async getSystemStatus(): Promise<any> {
    const response = await adminApi.get('/admin/system/status');
    return response.data;
  }

  async markAlertAsRead(alertId: number): Promise<void> {
    await adminApi.put(`/admin/alerts/${alertId}/read`);
  }

  // Customers
  async getCustomers(params?: {
    search?: string;
    status?: string;
    vip?: boolean;
    page?: number;
    pageSize?: number;
  }): Promise<{ customers: Customer[]; total: number }> {
    const response = await adminApi.get('/admin/customers', { params });
    return response.data;
  }

  async getCustomer(id: number): Promise<Customer> {
    const response = await adminApi.get(`/admin/customers/${id}`);
    return response.data;
  }

  async createCustomer(customer: Partial<Customer>): Promise<Customer> {
    const response = await adminApi.post('/admin/customers', customer);
    return response.data;
  }

  async updateCustomer(id: number, customer: Partial<Customer>): Promise<Customer> {
    const response = await adminApi.put(`/admin/customers/${id}`, customer);
    return response.data;
  }

  async deleteCustomer(id: number): Promise<void> {
    await adminApi.delete(`/admin/customers/${id}`);
  }

  // Bookings
  async getBookings(params?: {
    search?: string;
    status?: string;
    bookingType?: string; // NEW: Filter by Quote, Regular, Option, Manual
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ bookings: Booking[]; total: number; pagination?: any }> {
    const response = await adminApi.get('/admin/bookings', { params });
    return response.data;
  }

  async getBooking(id: number): Promise<Booking> {
    const response = await adminApi.get(`/admin/bookings/${id}`);
    return response.data;
  }

  async updateBooking(id: number, booking: Partial<Booking>): Promise<Booking> {
    const response = await adminApi.put(`/admin/bookings/${id}`, booking);
    return response.data;
  }

  async updateBookingStatus(id: number, status: string, quoteValidUntil?: string): Promise<any> {
    const response = await adminApi.put(`/admin/bookings/${id}/status`, {
      status,
      quoteValidUntil: quoteValidUntil ? new Date(quoteValidUntil).toISOString() : undefined
    });
    return response.data;
  }

  async cancelBooking(id: number, reason?: string): Promise<void> {
    await adminApi.post(`/admin/bookings/${id}/cancel`, { reason });
  }

  // ========================================
  // ADMIN BOOKING ENDPOINTS - Gemäß Backend API
  // ========================================

  /**
   * 3. PUT /api/admin/bookings/{id} - Buchung bearbeiten
   * Backend erwartet: ParticipantCount, TotalAmount, Extras, MealSelections
   */
  async updateAdminBooking(id: number, data: {
    numberOfGuests: number;
    mealSelections: { mealId: string; quantity: number }[];
    selectedExtraIds: number[];
    totalAmount?: number;
    // Teilnehmer-Kategorien (Anlage v1.3 Abschnitt 2.1), optional
    adultCount?: number;
    childCount?: number;
    childSmallCount?: number;
    infantCount?: number;
    freeParticipantWithSeatCount?: number;
    freeParticipantNoSeatCount?: number;
    // Anzahlungssatz-Übersteuerung in Dezimalform (0.40 = 40%), 0 = zurücksetzen
    depositPercentageOverride?: number;
  }): Promise<any> {
    // Map to backend field names
    const backendData = {
      participantCount: data.numberOfGuests,
      totalAmount: data.totalAmount || 0,
      extras: data.selectedExtraIds.map(id => id.toString()),
      mealSelections: data.mealSelections.map((m, index) => ({
        participantIndex: index,
        mealType: m.mealId || 'standard',
        dietaryNotes: null
      })),
      // Teilnehmer-Kategorien durchreichen (undefined-Werte werden von axios weggelassen)
      adultCount: data.adultCount,
      childCount: data.childCount,
      childSmallCount: data.childSmallCount,
      infantCount: data.infantCount,
      freeParticipantWithSeatCount: data.freeParticipantWithSeatCount,
      freeParticipantNoSeatCount: data.freeParticipantNoSeatCount,
      depositPercentageOverride: data.depositPercentageOverride
    };
    const response = await adminApi.put(`/admin/bookings/${id}`, backendData);
    return response.data;
  }

  // ===== BLANKOPOSITIONEN (Anlage v1.3 Abschnitt 2.5 Punkt 8) =====

  /**
   * Blankopositionen einer Buchung auflisten
   */
  async getBookingCustomItems(bookingId: number): Promise<BookingCustomItem[]> {
    const response = await adminApi.get(`/admin/bookings/${bookingId}/custom-items`);
    return response.data;
  }

  /**
   * Blankoposition hinzufügen (Text, Bruttopreis, Steuersatz frei wählbar).
   * Der Buchungsgesamtpreis wird serverseitig um den Positionsbetrag erhöht.
   */
  async addBookingCustomItem(bookingId: number, data: {
    description: string;
    quantity: number;
    grossPrice: number;
    taxRate: number; // Dezimalwert: 0.19, 0.07 oder 0
  }): Promise<BookingCustomItem> {
    const response = await adminApi.post(`/admin/bookings/${bookingId}/custom-items`, data);
    return response.data;
  }

  /**
   * Blankoposition entfernen (Gesamtpreis wird serverseitig reduziert)
   */
  async deleteBookingCustomItem(bookingId: number, itemId: number): Promise<void> {
    await adminApi.delete(`/admin/bookings/${bookingId}/custom-items/${itemId}`);
  }

  /**
   * 4. PATCH /api/admin/bookings/{id}/status - Status ändern
   */
  async changeBookingStatus(id: number, newStatus: string, reason: string): Promise<any> {
    const response = await adminApi.patch(`/admin/bookings/${id}/status`, {
      newStatus,
      reason
    });
    return response.data;
  }

  /**
   * 5. POST /api/admin/bookings/{id}/cancel - Stornieren mit Rückerstattung
   */
  async cancelBookingWithRefund(id: number, cancellation: {
    reason: string;
    refundAmount: number;
    refundMethod: string;
    internalNote?: string;
    sendConfirmation?: boolean;
  }): Promise<{ success: boolean; message: string; refundedAmount: number }> {
    const response = await adminApi.post(`/admin/bookings/${id}/cancel`, {
      reason: cancellation.reason,
      refundAmount: cancellation.refundAmount,
      refundMethod: cancellation.refundMethod,
      internalNote: cancellation.internalNote || '',
      sendConfirmation: cancellation.sendConfirmation ?? true
    });
    return response.data;
  }

  /**
   * 6. GET /api/admin/bookings/{id}/notes - Notizen abrufen
   */
  async getBookingNotes(id: number): Promise<{
    id: number;
    bookingId: number;
    text: string;
    type: string;
    createdBy: string;
    createdAt: string;
    isReminder: boolean;
    reminderDate?: string;
    reminderCompleted: boolean;
  }[]> {
    const response = await adminApi.get(`/admin/bookings/${id}/notes`);
    return response.data;
  }

  /**
   * 7. POST /api/admin/bookings/{id}/notes - Notiz hinzufügen
   */
  async addBookingNote(id: number, note: {
    text: string;
    type: string;
    isReminder: boolean;
    reminderDate?: string;
  }): Promise<any> {
    const response = await adminApi.post(`/admin/bookings/${id}/notes`, note);
    return response.data;
  }

  /**
   * 8. DELETE /api/admin/bookings/{id}/notes/{noteId} - Notiz löschen
   */
  async deleteBookingNote(bookingId: number, noteId: number): Promise<void> {
    await adminApi.delete(`/admin/bookings/${bookingId}/notes/${noteId}`);
  }

  // ========================================
  // BOOKING ADDONS ENDPOINTS
  // ========================================

  /**
   * GET /api/addon/bookings/{bookingId}/addons - AddOns einer Buchung abrufen
   */
  async getBookingAddons(bookingId: number): Promise<BookingAddOnDto[]> {
    const response = await adminApi.get(`/addon/bookings/${bookingId}/addons`);
    return response.data;
  }

  /**
   * PUT /api/addon/bookings/addons/{addonId} - AddOn aktualisieren
   */
  async updateBookingAddon(addonId: number, data: { quantity: number; specialRequests?: string }): Promise<BookingAddOnDto> {
    const response = await adminApi.put(`/addon/bookings/addons/${addonId}`, data);
    return response.data;
  }

  /**
   * POST /api/addon/bookings/{bookingId}/addons - AddOn hinzufügen
   */
  async addBookingAddon(bookingId: number, addon: {
    addOnServiceId: number;
    quantity: number;
    specialRequests?: string;
  }): Promise<BookingAddOnDto> {
    const response = await adminApi.post(`/addon/bookings/${bookingId}/addons`, addon);
    return response.data;
  }

  /**
   * DELETE /api/addon/bookings/addons/{addonId} - AddOn entfernen
   */
  async deleteBookingAddon(addonId: number): Promise<void> {
    await adminApi.delete(`/addon/bookings/addons/${addonId}`);
  }

  /**
   * GET /api/addon/bookings/{bookingId}/addons/total - AddOn-Gesamtpreis
   */
  async getBookingAddonsTotal(bookingId: number): Promise<{ bookingId: number; totalAddOnPrice: number }> {
    const response = await adminApi.get(`/addon/bookings/${bookingId}/addons/total`);
    return response.data;
  }

  /**
   * POST /api/tour-types/{tourType}/addons/calculate - Berechnete AddOns für TourType
   * Gibt CalculatedAddonDto[] mit Kategorisierungs-Infos zurück
   */
  async calculateAddonsForTourType(tourType: string, participantCount: number): Promise<CalculatedAddonDto[]> {
    const response = await adminApi.post(`/tour-types/${tourType}/addons/calculate`, {
      tourType,
      participantCount
    });
    return response.data;
  }

  /**
   * 9. POST /api/admin/bookings/manual - Manuelle Buchung erstellen
   */
  async createManualBooking(booking: {
    eventDateId: string;
    variantId: string;
    numberOfGuests: number;
    guestName: string;
    email: string;
    phone: string;
    adminNote?: string;
  }): Promise<any> {
    const response = await adminApi.post('/admin/bookings/manual', booking);
    return response.data;
  }

  async checkAvailability(date: string, participantCount: number): Promise<{
    isAvailable: boolean;
    availableSpots: number;
    message?: string;
  }> {
    const response = await adminApi.get('/admin/availability', {
      params: { date, participantCount }
    });
    return response.data;
  }

  // Payments
  // Buchhaltungsrolle (Anlage v1.4 Abschnitt 2.7 Punkt 9): nutzt api/accounting/payments
  // (identisches Antwortformat), da api/admin/* der AdminOnly-Policy unterliegt
  private paymentsBasePath(): string {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        const role = payload.role || payload.Role;
        if (role === 'Accounting') return '/accounting/payments';
      }
    } catch {
      // Fallback auf Admin-Pfad
    }
    return '/admin/payments';
  }

  async getPayments(params?: {
    search?: string;
    status?: string;
    method?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ payments: Payment[]; total: number; summary: any }> {
    const response = await adminApi.get(this.paymentsBasePath(), { params });
    return response.data;
  }

  async getPayment(id: number): Promise<Payment> {
    const response = await adminApi.get(`${this.paymentsBasePath()}/${id}`);
    return response.data;
  }

  async recordManualPayment(payment: {
    bookingId: number;
    amount: number;
    paymentType: string;
    paymentMethod: string;
    notes?: string;
  }): Promise<Payment> {
    const response = await adminApi.post('/admin/payments/manual', payment);
    return response.data;
  }

  async refundPayment(id: number, amount: number, reason?: string): Promise<void> {
    await adminApi.post(`/admin/payments/${id}/refund`, { amount, reason });
  }

  // Schedules/Calendar
  async getSchedules(params?: {
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{ schedules: Schedule[]; count: number }> {
    const response = await adminApi.get('/admin/schedules', { params });
    return response.data;
  }

  async getCalendarSlots(year: number, month: number): Promise<any> {
    const response = await adminApi.get(`/admin/calendar/slots`, {
      params: { year, month }
    });
    return response.data;
  }

  async createSchedule(schedule: Partial<Schedule>): Promise<Schedule> {
    const response = await adminApi.post('/admin/schedules', schedule);
    return response.data;
  }

  async updateSchedule(id: number, schedule: Partial<Schedule>): Promise<Schedule> {
    const response = await adminApi.put(`/admin/schedules/${id}`, schedule);
    return response.data;
  }

  async deleteSchedule(id: number): Promise<void> {
    await adminApi.delete(`/admin/schedules/${id}`);
  }

  // Reports
  async getReport(reportType: string, params?: {
    dateFrom?: string;
    dateTo?: string;
    groupBy?: string;
  }): Promise<ReportData> {
    const response = await adminApi.get(`/admin/reports/${reportType}`, { params });
    return response.data;
  }

  async exportReport(reportType: string, format: 'csv' | 'pdf', params?: any): Promise<Blob> {
    const response = await adminApi.get(`/admin/reports/${reportType}/export`, {
      params: { ...params, format },
      responseType: 'blob'
    });
    return response.data;
  }

  // Settings (Legacy)
  async getSettings(): Promise<SystemSettings> {
    const response = await adminApi.get('/admin/settings');
    return response.data;
  }

  async updateSettings(settings: Partial<SystemSettings>): Promise<SystemSettings> {
    const response = await adminApi.put('/admin/settings', settings);
    return response.data;
  }

  async testEmailSettings(settings: any): Promise<{ success: boolean; message: string }> {
    const response = await adminApi.post('/admin/settings/test-email', settings);
    return response.data;
  }

  async createBackup(): Promise<{ backupId: string; path: string }> {
    const response = await adminApi.post('/admin/settings/backup');
    return response.data;
  }

  async restoreBackup(backupId: string): Promise<void> {
    await adminApi.post(`/admin/settings/restore/${backupId}`);
  }

  // ========================================
  // SYSTEM SETTINGS - Company Info & Invoice Data
  // ========================================

  /**
   * Get company settings (for invoices, PDFs, admin settings)
   * GET /api/systemsettings
   */
  async getCompanySettings(): Promise<CompanySettings> {
    const response = await adminApi.get('/systemsettings');
    return response.data;
  }

  /**
   * Update company settings
   * PUT /api/systemsettings
   */
  async updateCompanySettings(settings: Partial<CompanySettings>): Promise<CompanySettings> {
    const response = await adminApi.put('/systemsettings', settings);
    return response.data;
  }

  /**
   * Upload company logo
   * POST /api/systemsettings/logo
   */
  async uploadCompanyLogo(file: File): Promise<{ logoPath: string; message: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await adminApi.post('/systemsettings/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  /**
   * Get public settings (no auth required)
   * GET /api/systemsettings/public
   */
  async getPublicSettings(): Promise<{
    companyName: string;
    companyPhone: string;
    companyEmail: string;
    companyWebsite: string;
    companyAddress: string;
    logoPath: string;
  }> {
    const response = await adminApi.get('/systemsettings/public');
    return response.data;
  }

  // Trips Overview
  async getTripsOverview(dateFrom?: string, dateTo?: string): Promise<any> {
    const response = await adminApi.get('/admin/trips-overview', {
      params: { dateFrom, dateTo }
    });
    return response.data;
  }

  // Users Management
  async getUsers(params?: {
    search?: string;
    role?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ users: any[]; total: number }> {
    const response = await adminApi.get('/admin/users', { params });
    return response.data;
  }

  async createUser(user: any): Promise<any> {
    const response = await adminApi.post('/admin/users', user);
    return response.data;
  }

  async updateUser(id: number, user: any): Promise<any> {
    const response = await adminApi.put(`/admin/users/${id}`, user);
    return response.data;
  }

  async deleteUser(id: number): Promise<void> {
    await adminApi.delete(`/admin/users/${id}`);
  }

  async resetUserPassword(id: number): Promise<{ newPassword: string }> {
    const response = await adminApi.post(`/admin/users/${id}/reset-password`);
    return response.data;
  }

  // ========================================
  // ADDON MANAGEMENT - Zusatzoptionen
  // ========================================

  /**
   * Get all AddOn categories with their services
   */
  async getAddOnCategories(includeInactive: boolean = false): Promise<AddOnCategory[]> {
    const response = await adminApi.get('/addon/categories', {
      params: { includeInactive }
    });
    return response.data;
  }

  /**
   * Get single AddOn category by ID with all services
   */
  async getAddOnCategory(id: number): Promise<AddOnCategory> {
    const response = await adminApi.get(`/addon/categories/${id}`);
    return response.data;
  }

  /**
   * Create new AddOn category
   */
  async createAddOnCategory(category: CreateAddOnCategoryDto): Promise<AddOnCategory> {
    const response = await adminApi.post('/addon/categories', category);
    return response.data;
  }

  /**
   * Update AddOn category
   */
  async updateAddOnCategory(id: number, category: UpdateAddOnCategoryDto): Promise<AddOnCategory> {
    const response = await adminApi.put(`/addon/categories/${id}`, category);
    return response.data;
  }

  /**
   * Delete AddOn category (soft delete)
   */
  async deleteAddOnCategory(id: number): Promise<boolean> {
    await adminApi.delete(`/addon/categories/${id}`);
    return true;
  }

  /**
   * Get services for a category
   */
  async getAddOnServicesByCategory(categoryId: number, includeInactive: boolean = false): Promise<AddOnService[]> {
    const response = await adminApi.get(`/addon/categories/${categoryId}/services`, {
      params: { includeInactive }
    });
    return response.data;
  }

  /**
   * Get single AddOn service by ID
   */
  async getAddOnService(id: number): Promise<AddOnService> {
    const response = await adminApi.get(`/addon/services/${id}`);
    return response.data;
  }

  /**
   * Create new AddOn service
   */
  async createAddOnService(service: CreateAddOnServiceDto): Promise<AddOnService> {
    const response = await adminApi.post('/addon/services', service);
    return response.data;
  }

  /**
   * Update AddOn service
   */
  async updateAddOnService(id: number, service: UpdateAddOnServiceDto): Promise<AddOnService> {
    const response = await adminApi.put(`/addon/services/${id}`, service);
    return response.data;
  }

  /**
   * Delete AddOn service (soft delete)
   */
  async deleteAddOnService(id: number): Promise<boolean> {
    await adminApi.delete(`/addon/services/${id}`);
    return true;
  }

  // ========================================
  // COMPOSITE ADDON MANAGEMENT - Phase 1 Backend
  // ========================================

  /**
   * Get all compositions for a composite add-on
   * GET /api/admin/addons/{id}/compositions
   */
  async getCompositions(addOnId: number): Promise<CompositeAddOnComponent[]> {
    const response = await adminApi.get(`/admin/addons/${addOnId}/compositions`);
    return response.data;
  }

  /**
   * Add a component to a composite add-on
   * POST /api/admin/addons/{id}/compositions
   */
  async addComposition(addOnId: number, component: CreateCompositeComponentDto): Promise<CompositeAddOnComponent> {
    const response = await adminApi.post(`/admin/addons/${addOnId}/compositions`, component);
    return response.data;
  }

  /**
   * Update a component of a composite add-on
   * PUT /api/admin/addons/{id}/compositions/{compositionId}
   */
  async updateComposition(
    addOnId: number,
    compositionId: number,
    component: UpdateCompositeComponentDto
  ): Promise<CompositeAddOnComponent> {
    const response = await adminApi.put(`/admin/addons/${addOnId}/compositions/${compositionId}`, component);
    return response.data;
  }

  /**
   * Delete a component from a composite add-on
   * DELETE /api/admin/addons/{id}/compositions/{compositionId}
   */
  async deleteComposition(addOnId: number, compositionId: number): Promise<void> {
    await adminApi.delete(`/admin/addons/${addOnId}/compositions/${compositionId}`);
  }

  /**
   * Get calculated price for a composite add-on
   * GET /api/admin/addons/{id}/calculated-price
   */
  async getCalculatedPrice(addOnId: number): Promise<CalculatedPriceResponse> {
    const response = await adminApi.get(`/admin/addons/${addOnId}/calculated-price`);
    return response.data;
  }

  /**
   * Get calculated price with detailed tax breakdown
   * GET /api/admin/addons/{id}/calculated-price-with-tax
   */
  async getCalculatedPriceWithTax(addOnId: number): Promise<CalculatedPriceWithTaxDto> {
    const response = await adminApi.get(`/admin/addons/${addOnId}/calculated-price-with-tax`);
    return response.data;
  }

  /**
   * Get add-on by ID with compositions
   * GET /api/admin/addons/{id}
   */
  async getAddOn(id: number): Promise<AddOnService> {
    const response = await adminApi.get(`/admin/addons/${id}`);
    return response.data;
  }

  /**
   * Get all add-ons (for use in AddCompositionDialog)
   */
  async getAllAddOns(includeInactive: boolean = false): Promise<AddOnService[]> {
    const categories = await this.getAddOnCategories(includeInactive);
    const allServices: AddOnService[] = [];
    categories.forEach(cat => {
      if (cat.services) {
        allServices.push(...cat.services);
      }
    });
    return allServices;
  }

  // ========================================
  // TOUR TYPE ENDPOINTS
  // ========================================

  /**
   * Get all tour types
   * GET /api/tour-types
   */
  async getTourTypes(includeInactive: boolean = false): Promise<TourType[]> {
    const response = await adminApi.get('/tour-types', {
      params: { includeInactive }
    });
    return response.data;
  }

  // ========================================
  // QUOTE ENDPOINTS (#35 - Angebote erstellen)
  // ========================================

  /**
   * Get list of quotes with filtering
   * GET /api/quote
   */
  async getQuotes(filter?: QuoteFilter): Promise<QuoteListResponse> {
    const response = await adminApi.get('/quote', { params: filter });
    return response.data;
  }

  /**
   * Get single quote by ID
   * GET /api/quote/{id}
   */
  async getQuote(id: number): Promise<Quote> {
    const response = await adminApi.get(`/quote/${id}`);
    return response.data;
  }

  /**
   * Create new quote
   * POST /api/quote
   */
  async createQuote(quote: CreateQuoteDto): Promise<Quote> {
    const response = await adminApi.post('/quote', quote);
    return response.data;
  }

  /**
   * Update existing quote
   * PUT /api/quote/{id}
   */
  async updateQuote(id: number, quote: UpdateQuoteDto): Promise<Quote> {
    const response = await adminApi.put(`/quote/${id}`, quote);
    return response.data;
  }

  /**
   * Delete quote (only Draft status)
   * DELETE /api/quote/{id}
   */
  async deleteQuote(id: number): Promise<void> {
    await adminApi.delete(`/quote/${id}`);
  }

  /**
   * Generate PDF for quote
   * POST /api/quote/{id}/pdf
   */
  async generateQuotePdf(id: number): Promise<{ message: string; size: number }> {
    const response = await adminApi.post(`/quote/${id}/pdf`);
    return response.data;
  }

  /**
   * Download PDF for quote
   * GET /api/quote/{id}/pdf
   */
  async downloadQuotePdf(id: number): Promise<Blob> {
    const response = await adminApi.get(`/quote/${id}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  }

  /**
   * Send quote via email
   * POST /api/quote/{id}/send
   */
  async sendQuoteEmail(id: number, options?: SendQuoteEmail): Promise<{ message: string }> {
    const response = await adminApi.post(`/quote/${id}/send`, options || {});
    return response.data;
  }

  /**
   * Update quote status
   * PATCH /api/quote/{id}/status
   */
  async updateQuoteStatus(id: number, update: QuoteStatusUpdate): Promise<Quote> {
    const response = await adminApi.patch(`/quote/${id}/status`, update);
    return response.data;
  }

  /**
   * Convert quote to booking
   * POST /api/quote/{id}/convert
   */
  async convertQuoteToBooking(id: number): Promise<{ message: string; bookingId: number }> {
    const response = await adminApi.post(`/quote/${id}/convert`);
    return response.data;
  }

  /**
   * Calculate price preview for quote configuration
   * POST /api/quote/price-preview
   */
  async getQuotePricePreview(preview: QuotePricePreview): Promise<QuotePricePreviewResponse> {
    const response = await adminApi.post('/quote/price-preview', preview);
    return response.data;
  }

  // ============================================================
  // Order Lists & Boarding Tickets
  // ============================================================

  /**
   * Download order list PDF for a specific date
   * GET /api/admin/reports/order-list/pdf?date=...
   */
  async downloadOrderListPdf(date: string): Promise<void> {
    const response = await adminApi.get(`/admin/reports/order-list/pdf?date=${date}`, {
      responseType: 'blob'
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Bestellliste-${date}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  /**
   * Download boarding ticket PDF for a specific booking
   * GET /api/admin/bookings/{bookingId}/boarding-ticket
   */
  async downloadBoardingTicketPdf(bookingId: number, bookingNumber: string): Promise<void> {
    const response = await adminApi.get(`/admin/bookings/${bookingId}/boarding-ticket`, {
      responseType: 'blob'
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Bordticket-${bookingNumber}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

// Export singleton instance
export const adminService = new AdminService();
export default adminService;
